// app/frontend/pages/DanceStyles/DanceStyleIndex.tsx
import React, { useState, useEffect } from 'react';
import Helmet from 'react-helmet';
import {
  Container,
  Title,
  Button,
  Card,
  Text,
  Group,
  SimpleGrid,
  ActionIcon,
  Stack,
  MantineProvider,
  TextInput,
  Box,
  Image,
  Loader,
  Skeleton,
} from '@mantine/core';
import { Link, router } from '@inertiajs/react';
import { usePage } from '@inertiajs/react';
import {
  IconPlus,
  IconSearch,
  IconArrowRight,
  IconCalendar,
  IconCalendarOff,
  IconUsers,
  IconEdit,
} from '@tabler/icons-react';
import { useDebouncedValue } from '@mantine/hooks';
import { StablePagination } from '../../components/StablePagination';
import baseImage from '../../assets/images/DANCE.jpg';

import BannerAd from '../../components/Ads/BannerAd';

import { DanceStyleCard } from '../../components/ui';

interface DanceStyle {
  id: number;
  name: string;
  slug: string;
  seo_image_url?: string;
  cover_image_url?: string;
  upcoming_events_count: number;
  past_events_count: number;
  artists_count: number;
  created_at: string;
}

interface BannerAd {
  id: number;
  title: string;
  description?: string;
  url: string;
  banner_url?: string;
  ad_type: string;
}

interface Pagination {
  current_page: number;
  total_pages: number;
  total_count: number;
  per_page: number;
}

interface DanceStyleIndexProps {
  banner_ads?: BannerAd[];
  dance_styles: DanceStyle[];
  can_edit: boolean;
  pagination: Pagination;
  page_meta?: {
    title: string;
    description: string;
    url: string;
    image: string;
  };
}

const DanceStyleIndex: React.FC<DanceStyleIndexProps> = ({
  dance_styles,
  can_edit,
  pagination,
  page_meta,
  banner_ads = [],
}) => {
  // State for search and loading
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useDebouncedValue(searchQuery, 300);
  const [isLoading, setIsLoading] = useState(false);
  const debouncingSearch = searchQuery !== debouncedSearch;

  // Handle pagination change
  const handlePageChange = (page: number) => {
    setIsLoading(true);
    router.get(
      '/dance_styles',
      { page: page, query: debouncedSearch },
      {
        preserveState: true,
        onSuccess: () => setIsLoading(false),
      },
    );
  };

  useEffect(() => {
    // If you need to manually update the document title and meta tags
    if (page_meta?.title) {
      document.title = page_meta.title;
    }
    // console.log('🤓 DS Index Page meta:', page_meta);
    // Optional: Update meta description directly if Helmet doesn't work
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription && page_meta?.description) {
      metaDescription.setAttribute('content', page_meta.description);
    }
  }, [page_meta]);

  // Handle search change
  useEffect(() => {
    if (debouncedSearch !== searchQuery) return;

    setIsLoading(true);
    router.get(
      '/dance_styles',
      { page: 1, query: debouncedSearch },
      {
        preserveState: true,
        onSuccess: () => setIsLoading(false),
      },
    );
  }, [debouncedSearch]);

  return (
    <MantineProvider>
      {page_meta && (
        <Helmet>
          <title>{page_meta.title}</title>
          <meta name="description" content={page_meta.description} />

          {/* Open Graph tags */}
          <meta property="og:title" content={page_meta.title} />
          <meta property="og:description" content={page_meta.description} />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={page_meta.url} />
          <meta property="og:image" content={page_meta.image} />

          {/* Twitter Card tags */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={page_meta.title} />
          <meta name="twitter:description" content={page_meta.description} />
          <meta name="twitter:image" content={page_meta.image} />
        </Helmet>
      )}
      <Container size="lg" py="xl">
        {banner_ads && banner_ads.length > 0 && (
          <div style={{ padding: '0px' }}>
            <BannerAd ads={banner_ads} />
          </div>
        )}
        <Title order={1} mb="md" className="poppins mt-3" style={{ color: '#f2f2f2', lineHeight: '1' }}>
          Global Dance Style Calendars
        </Title>

        <Box
          className="filter-wrapper"
          style={{
            position: 'sticky',
            top: '8px',
            zIndex: 100,
            backgroundColor: '#75757550',
            padding: '16px',
            marginBottom: '1.5rem',
            borderRadius: '12px',
            margin: '12px 8px 1.5rem 0',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), 0 2px 8px rgba(0, 0, 0, 0.2)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(8px)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', width: '100%' }}>
            <TextInput
              placeholder="Search dance styles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.currentTarget.value)}
              leftSection={<IconSearch size={16} />}
              style={{ flex: 1, minWidth: 0 }}
              styles={{
                input: {
                  backgroundColor: '#2c2c2c',
                  color: '#f2f2f2',
                  borderColor: '#f2f2f250',
                },
                label: {
                  color: '#f2f2f2',
                },
              }}
              className="poppins"
            />

            {can_edit && (
              <Box style={{ flexShrink: 0 }}>
                <Button
                  component={Link}
                  href="/dance_styles/new"
                  leftSection={<IconPlus size={16} />}
                  color="#EA580C"
                  className="poppins"
                  style={{
                    borderRadius: '8px',
                  }}
                >
                  Add New Dance Style
                </Button>
              </Box>
            )}
          </div>
        </Box>

        {pagination.total_pages > 1 && (
          <Box my="lg" style={{ display: 'flex', justifyContent: 'left' }}>
            <StablePagination
              total={pagination.total_pages}
              value={pagination.current_page}
              onChange={handlePageChange}
              color="orange"
            />
          </Box>
        )}

        <Group mb="0" align="flex-end">
          <Text size="sm" style={{ color: '#f2f2f2' }} className="poppins mb-4" justify="left">
            Showing {Math.min(pagination.per_page, dance_styles.length)} of {pagination.total_count} dance styles
            {(isLoading || debouncingSearch) && <Loader color="orange" size="xs" ml="md" />}
          </Text>
        </Group>

        {isLoading ? (
          <Box py="xl" style={{ display: 'flex', justifyContent: 'center' }}></Box>
        ) : (
          <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing="sm">
            {dance_styles.map((danceStyle) => (
              <DanceStyleCard
                key={danceStyle.id}
                danceStyle={danceStyle}
                canEdit={can_edit}
                onCardClick={(style) => {
                  window.location.href = `/dance_styles/${style.slug}`;
                }}
              />
            ))}
          </SimpleGrid>
        )}

        {pagination.total_pages > 1 && (
          <Box my="lg" style={{ display: 'flex', justifyContent: 'flex-start' }}>
            <StablePagination
              total={pagination.total_pages}
              value={pagination.current_page}
              onChange={handlePageChange}
              color="orange"
            />
          </Box>
        )}
      </Container>
    </MantineProvider>
  );
};

export default DanceStyleIndex;
